import React, { useState, useEffect } from 'react';
import { X, WifiOff, AlertTriangle, Battery, Signal, Satellite, Search } from 'lucide-react';
import { useNotifications } from '../contexts/NotificationContext';
import { NOTIFICATION_TYPES } from '../contexts/NotificationContext';

/**
 * RobotOfflinePopup Component
 * 
 * Shows centered modal popup notifications for robot offline alerts.
 * Includes the predicted cause of disconnection based on last-known telemetry analysis.
 * Displayed for ALL user types and persists until manually dismissed.
 */
const RobotOfflinePopup = () => {
  const { notifications, removeNotification, markAsRead } = useNotifications();
  const [dismissedNotifications, setDismissedNotifications] = useState(new Set());

  // Filter to only show unread ROBOT_OFFLINE notifications that are persistent
  const offlineNotifications = notifications.filter(notif => {
    if (notif.type !== NOTIFICATION_TYPES.ROBOT_OFFLINE) return false;
    if (notif.isRead === true) return false;
    // Only persistent notifications (duration = 0 or undefined)
    if (notif.duration !== undefined && notif.duration !== null && typeof notif.duration === 'number' && notif.duration > 0) return false;
    if (dismissedNotifications.has(notif.id)) return false;
    // Also check metadata key
    if (notif.metadata && notif.metadata.robotId) {
      const metadataKey = `offline_${notif.metadata.robotId}`;
      if (dismissedNotifications.has(metadataKey)) return false;
    }
    return true;
  });

  useEffect(() => {
    const allOffline = notifications.filter(n => n.type === NOTIFICATION_TYPES.ROBOT_OFFLINE);
    if (allOffline.length > 0) {
      console.log('[RobotOfflinePopup] Offline notifications:', {
        total: allOffline.length,
        unread: allOffline.filter(n => !n.isRead).length,
        showing: offlineNotifications.length
      });
    }
  }, [notifications, offlineNotifications]);

  if (offlineNotifications.length === 0) return null;

  const handleDismiss = async (notificationId) => {
    const notification = notifications.find(n => n.id === notificationId);

    setDismissedNotifications(prev => new Set(prev).add(notificationId));

    if (notification && notification.metadata?.robotId) {
      const metadataKey = `offline_${notification.metadata.robotId}`;
      setDismissedNotifications(prev => new Set(prev).add(metadataKey));
    }

    try {
      await markAsRead(notificationId);
    } catch (error) {
      console.error('Error marking offline notification as read:', error);
    }

    await removeNotification(notificationId);
  };

  const formatTimestamp = (timestamp) => {
    const now = new Date();
    const diff = now - new Date(timestamp);
    const seconds = Math.floor(diff / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);

    if (seconds < 60) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return new Date(timestamp).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  // Get confidence badge color
  const getConfidenceBadge = (confidence) => {
    switch (confidence) {
      case 'HIGH': return 'bg-red-100 text-red-700';
      case 'MEDIUM': return 'bg-yellow-100 text-yellow-700';
      default: return 'bg-gray-100 text-gray-600';
    }
  };

  // Show one notification at a time
  const current = offlineNotifications[0];
  if (!current) return null;

  const meta = current.metadata || {};
  const predictedCause = meta.predictedCause;
  const causeConfidence = meta.causeConfidence;
  const causeSummary = meta.causeSummary;
  const causeDetails = meta.causeDetails;
  const robotName = meta.robotName || meta.robotId || 'Unknown Robot';

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-[99999]"
        onClick={(e) => e.stopPropagation()}
      />

      {/* Centered Modal */}
      <div className="fixed inset-0 flex items-center justify-center z-[100000] p-2 sm:p-4 pointer-events-none">
        <div className="bg-white rounded-lg shadow-2xl max-w-lg w-full mx-auto pointer-events-auto animate-in zoom-in duration-200 max-h-[calc(100vh-1rem)] sm:max-h-[calc(100vh-2rem)] overflow-y-auto">
          {/* Header */}
          <div className="bg-red-50 border-b border-red-200 px-4 sm:px-6 py-3 sm:py-4 rounded-t-lg sticky top-0 z-10">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2 sm:space-x-3 min-w-0">
                <div className="flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-red-100 flex items-center justify-center text-red-600">
                  <WifiOff className="h-4 w-4 sm:h-6 sm:w-6" />
                </div>
                <div className="min-w-0">
                  <h3 className="text-sm sm:text-lg font-semibold text-red-900 truncate">
                    {current.title}
                  </h3>
                  <span className="inline-flex items-center px-1.5 sm:px-2 py-0.5 rounded text-[10px] sm:text-xs font-medium bg-red-200 text-red-800 mt-0.5 sm:mt-1">
                    <AlertTriangle className="h-2.5 w-2.5 sm:h-3 sm:w-3 mr-0.5 sm:mr-1" />
                    Critical Alert
                  </span>
                </div>
              </div>

              <button
                onClick={() => handleDismiss(current.id)}
                className="flex-shrink-0 text-red-400 hover:text-red-600 transition-colors p-1 hover:bg-red-100 rounded"
                title="Close"
              >
                <X className="h-5 w-5 sm:h-6 sm:w-6" />
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="px-4 sm:px-6 py-3 sm:py-4">
            {/* Robot info */}
            <div className="mb-2 sm:mb-3">
              <p className="text-xs sm:text-sm text-gray-700">
                <span className="font-medium">Robot:</span> {robotName}
              </p>
              {meta.lastSeenTime && (
                <p className="text-xs sm:text-sm text-gray-600">
                  <span className="font-medium">Last Seen:</span> {meta.lastSeenTime}
                </p>
              )}
            </div>

            {/* Predicted Cause */}
            {predictedCause && predictedCause !== 'Unknown' && (
              <div className="mb-3 sm:mb-4 p-2.5 sm:p-3 bg-orange-50 rounded-lg border border-orange-200">
                <div className="flex items-center flex-wrap gap-1 mb-1.5 sm:mb-2">
                  <Search className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-orange-600 mr-1 sm:mr-2" />
                  <span className="text-xs sm:text-sm font-semibold text-orange-900">Predicted Cause</span>
                  {causeConfidence && (
                    <span className={`px-1.5 sm:px-2 py-0.5 rounded text-[10px] sm:text-xs font-medium ${getConfidenceBadge(causeConfidence)}`}>
                      {causeConfidence}
                    </span>
                  )}
                </div>
                <p className="text-sm sm:text-base font-bold text-orange-800 mb-0.5 sm:mb-1">{predictedCause}</p>
                {causeSummary && (
                  <p className="text-xs sm:text-sm text-orange-700">{causeSummary}</p>
                )}
              </div>
            )}

            {/* Last Known Telemetry */}
            {causeDetails && (
              <div className="mb-3 sm:mb-4">
                <p className="text-[10px] sm:text-xs font-semibold text-gray-700 mb-1.5 sm:mb-2 uppercase tracking-wide">Last Known Telemetry</p>
                <div className="grid grid-cols-2 gap-1.5 sm:gap-2">
                  {/* 12V Battery */}
                  {causeDetails.battery12V && (
                    <div className="p-1.5 sm:p-2 bg-gray-50 rounded-md border border-gray-200">
                      <div className="flex items-center text-[10px] sm:text-xs font-medium text-gray-700 mb-0.5 sm:mb-1">
                        <Battery className="h-2.5 w-2.5 sm:h-3 sm:w-3 mr-0.5 sm:mr-1" />
                        12V Battery
                      </div>
                      <p className="text-[10px] sm:text-xs text-gray-600">
                        SoC: <span className={`font-semibold ${causeDetails.battery12V.level !== null && causeDetails.battery12V.level <= 20 ? 'text-red-600' : 'text-gray-800'}`}>
                          {causeDetails.battery12V.level !== null ? `${causeDetails.battery12V.level.toFixed(1)}%` : 'N/A'}
                        </span>
                      </p>
                      <p className="text-[10px] sm:text-xs text-gray-600">
                        V: <span className={`font-semibold ${causeDetails.battery12V.voltage !== null && causeDetails.battery12V.voltage < 11.5 ? 'text-red-600' : 'text-gray-800'}`}>
                          {causeDetails.battery12V.voltage !== null ? `${causeDetails.battery12V.voltage.toFixed(1)}V` : 'N/A'}
                        </span>
                      </p>
                      {causeDetails.battery12V.temperature !== null && (
                        <p className="text-[10px] sm:text-xs text-gray-600">
                          T: <span className={`font-semibold ${causeDetails.battery12V.temperature >= 50 ? 'text-red-600' : 'text-gray-800'}`}>
                            {causeDetails.battery12V.temperature.toFixed(1)}°C
                          </span>
                        </p>
                      )}
                    </div>
                  )}

                  {/* 48V Battery */}
                  {causeDetails.battery48V && (
                    <div className="p-1.5 sm:p-2 bg-gray-50 rounded-md border border-gray-200">
                      <div className="flex items-center text-[10px] sm:text-xs font-medium text-gray-700 mb-0.5 sm:mb-1">
                        <Battery className="h-2.5 w-2.5 sm:h-3 sm:w-3 mr-0.5 sm:mr-1" />
                        48V Battery
                      </div>
                      <p className="text-[10px] sm:text-xs text-gray-600">
                        SoC: <span className={`font-semibold ${causeDetails.battery48V.level !== null && causeDetails.battery48V.level <= 20 ? 'text-red-600' : 'text-gray-800'}`}>
                          {causeDetails.battery48V.level !== null ? `${causeDetails.battery48V.level.toFixed(1)}%` : 'N/A'}
                        </span>
                      </p>
                      <p className="text-[10px] sm:text-xs text-gray-600">
                        V: <span className={`font-semibold ${causeDetails.battery48V.voltage !== null && causeDetails.battery48V.voltage < 46 ? 'text-red-600' : 'text-gray-800'}`}>
                          {causeDetails.battery48V.voltage !== null ? `${causeDetails.battery48V.voltage.toFixed(1)}V` : 'N/A'}
                        </span>
                      </p>
                      {causeDetails.battery48V.temperature !== null && (
                        <p className="text-[10px] sm:text-xs text-gray-600">
                          T: <span className={`font-semibold ${causeDetails.battery48V.temperature >= 50 ? 'text-red-600' : 'text-gray-800'}`}>
                            {causeDetails.battery48V.temperature.toFixed(1)}°C
                          </span>
                        </p>
                      )}
                    </div>
                  )}

                  {/* Modem / Signal */}
                  {causeDetails.modem && (
                    <div className="p-1.5 sm:p-2 bg-gray-50 rounded-md border border-gray-200">
                      <div className="flex items-center text-[10px] sm:text-xs font-medium text-gray-700 mb-0.5 sm:mb-1">
                        <Signal className="h-2.5 w-2.5 sm:h-3 sm:w-3 mr-0.5 sm:mr-1" />
                        Cellular
                      </div>
                      {causeDetails.modem.rssi_dbm !== null && (
                        <p className="text-[10px] sm:text-xs text-gray-600">
                          RSSI: <span className={`font-semibold ${causeDetails.modem.rssi_dbm <= -85 ? 'text-red-600' : 'text-gray-800'}`}>
                            {causeDetails.modem.rssi_dbm} dBm
                          </span>
                        </p>
                      )}
                      {causeDetails.modem.rsrp_dbm !== null && (
                        <p className="text-[10px] sm:text-xs text-gray-600">
                          RSRP: <span className={`font-semibold ${causeDetails.modem.rsrp_dbm <= -110 ? 'text-red-600' : 'text-gray-800'}`}>
                            {causeDetails.modem.rsrp_dbm} dBm
                          </span>
                        </p>
                      )}
                      {causeDetails.modem.sinr_db !== null && (
                        <p className="text-[10px] sm:text-xs text-gray-600">
                          SINR: <span className={`font-semibold ${causeDetails.modem.sinr_db <= 0 ? 'text-red-600' : 'text-gray-800'}`}>
                            {causeDetails.modem.sinr_db} dB
                          </span>
                        </p>
                      )}
                      {causeDetails.modem.rat && (
                        <p className="text-[10px] sm:text-xs text-gray-500">RAT: {causeDetails.modem.rat}</p>
                      )}
                    </div>
                  )}

                  {/* GNSS */}
                  {causeDetails.gnss && (
                    <div className="p-1.5 sm:p-2 bg-gray-50 rounded-md border border-gray-200">
                      <div className="flex items-center text-[10px] sm:text-xs font-medium text-gray-700 mb-0.5 sm:mb-1">
                        <Satellite className="h-2.5 w-2.5 sm:h-3 sm:w-3 mr-0.5 sm:mr-1" />
                        GNSS
                      </div>
                      {causeDetails.gnss.satellites !== null && (
                        <p className="text-[10px] sm:text-xs text-gray-600">
                          Sats: <span className={`font-semibold ${causeDetails.gnss.satellites <= 3 ? 'text-red-600' : 'text-gray-800'}`}>
                            {causeDetails.gnss.satellites}
                          </span>
                        </p>
                      )}
                      {causeDetails.gnss.hdop !== null && (
                        <p className="text-[10px] sm:text-xs text-gray-600">
                          HDOP: <span className={`font-semibold ${causeDetails.gnss.hdop > 5 ? 'text-red-600' : 'text-gray-800'}`}>
                            {causeDetails.gnss.hdop.toFixed(1)}
                          </span>
                        </p>
                      )}
                      {causeDetails.gnss.latitude && causeDetails.gnss.longitude && (
                        <p className="text-[10px] sm:text-xs text-gray-500 truncate">
                          {causeDetails.gnss.latitude.toFixed(4)}, {causeDetails.gnss.longitude.toFixed(4)}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Timestamp */}
            <p className="text-[10px] sm:text-xs text-gray-500">
              {formatTimestamp(current.timestamp)}
            </p>
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-4 sm:px-6 py-3 sm:py-4 rounded-b-lg border-t border-gray-200 sticky bottom-0">
            <div className="flex justify-between items-center">
              {offlineNotifications.length > 1 && (
                <span className="text-[10px] sm:text-xs text-gray-500">
                  {offlineNotifications.length} alert{offlineNotifications.length > 1 ? 's' : ''}
                </span>
              )}
              <button
                onClick={() => handleDismiss(current.id)}
                className="ml-auto px-4 sm:px-6 py-1.5 sm:py-2 bg-red-600 text-white text-sm rounded-md hover:bg-red-700 transition-colors font-medium"
              >
                I Understand
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default RobotOfflinePopup;

